import { canAccessAdminArea } from '../utils/adminAccess';
import React, { useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGlobalMetadata } from '../utils/providers';
import { useAuth } from '../utils/useAuth';
import { useMyCapabilities } from '../utils/metadata/capabilities';
import { loadRepOnlyNavigation } from '../utils/repOnlyNavigation';
import { queryPolicies } from '../utils/queryPolicies';
import { dashboardPath } from '../utils/metadata/dashboard-metadata';
import { reportPath } from './ReportPage/reportRoutes';
import { WORKFLOW_REPORTS } from './ReportPage/workflowReports';
import { useHoverPrefetch } from '../hooks/useHoverPrefetch';
import { useCanExportGridData } from '../utils/gridExportCapability';
import './Sidebar.css';

/* ── Icon paths (Feather-style, 24x24 viewBox) ─────────────────────── */
const ICON_PATHS: Record<string, string> = {
  home: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  layers: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  pin: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z',
  mail: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6',
  activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
  edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
  file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8',
  search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  settings: 'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
  document: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6',
  chart: 'M3 3v18h18M8 17V9M13 17V5M18 17v-6',
  kanban: 'M6 5v11M12 5v6M18 5v14',
  checklist: 'M3 17l2 2 4-4M3 7l2 2 4-4M13 6h8M13 12h8M13 18h8',
  dashboard: 'M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z',
  banknote: 'M2 6h20v12H2zM12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4M6 12h.01M18 12h.01',
  check: 'M20 6L9 17l-5-5',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  percent: 'M19 5L5 19M8.5 6.5a2 2 0 1 1-4 0 2 2 0 0 1 4 0M19.5 17.5a2 2 0 1 1-4 0 2 2 0 0 1 4 0',
  swap: 'M8 3L4 7l4 4M4 7h16M16 21l4-4-4-4M20 17H4',
  history: 'M3 3v5h5M3.05 13A9 9 0 1 0 6 5.3L3 8M12 7v5l4 2',
  message: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  inbox: 'M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
};

function SidebarIcon({ name, size = 13 }: { name: string; size?: number }) {
  const d = ICON_PATHS[name] || ICON_PATHS.document;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
    </svg>
  );
}

/** Where the logo goes, from anywhere in the app including /admin. */
const HOME_PATH = '/main-dashboard';

/* ── Route sub-segments (sidebar children) ──────────────────────────── */
interface RouteSegment {
  label: string;
  reportKey: string;
}

/* ── CORE navigation — ordered to match the target design ───────────── */
interface CoreNavItem {
  label: string;
  icon: string;
  /** Optional UX-only capability controlling whether this sidebar item renders. */
  capability?: string;
  /** Static path (frontend-only pages). */
  to?: string;
  /** Metadata table name — resolved to its route at render time. */
  table?: string;
  segments?: RouteSegment[];
}

type ReportNavItem =
  | {
      kind: 'dashboard';
      key: string;
      label: string;
      icon: string;
      capability?: string;
    }
  | {
      kind: 'report';
      key: string;
      label: string;
      icon: string;
    }
  | {
      kind: 'table';
      table: string;
      label: string;
      icon: string;
      capability?: string;
    }
  | {
      kind: 'page';
      to: string;
      label: string;
      icon: string;
      capability?: string;
    };

/**
 * Zack's agreed reporting information architecture.
 *
 * Report and dashboard entries resolve through published metadata, so an
 * unfinished destination stays hidden instead of becoming a dead hardcoded
 * route. Operational records reuse their registered table routes.
 */
const MAIN_NAV: Array<CoreNavItem | ReportNavItem> = [
  { kind: 'dashboard', key: 'business_command_center', label: 'Dashboard', icon: 'dashboard', capability: 'view_dashboard_navigation' },
  { label: 'Opportunities', icon: 'kanban', to: '/opportunities' },
  {
    label: 'Merchants', icon: 'home', table: 'merchant',
    segments: WORKFLOW_REPORTS
      .filter((report) => report.parentTable === 'merchant')
      .map((report) => ({ label: report.sidebarLabel, reportKey: report.key })),
  },
  {
    label: 'Submissions', icon: 'upload', table: 'submission',
    segments: WORKFLOW_REPORTS
      .filter((report) => report.parentTable === 'submission')
      .map((report) => ({ label: report.sidebarLabel, reportKey: report.key })),
  },
  { kind: 'dashboard', key: 'skyline_approvals', label: 'Approvals', icon: 'check', capability: 'view_approval_dashboard_navigation' },
  { kind: 'dashboard', key: 'skyline_funded', label: 'Funded', icon: 'banknote', capability: 'view_funded_dashboard_navigation' },
  // REG-743: the Friday payout list (reps see their own rows), receivables and payment matches.
  { kind: 'page', to: '/commissions', label: 'Commissions', icon: 'dollar' },
  { label: 'Rep', icon: 'user', table: 'rep' },
  { label: 'Funder List', icon: 'layers', table: 'funder', capability: 'view_funder_navigation' },
  { label: 'Lead Source', icon: 'pin', table: 'lead_source', capability: 'view_lead_source_navigation' },
  // Shown only with the lead_import capability; the route enforces it too.
  { label: 'Lead Imports', icon: 'inbox', table: 'lead_import', capability: 'lead_import' },
  // REG-771 GoACH debits (Core menu per Zack). Approvers (ach_approve) are all Exec/Dev, who also hold ach_initiate.
  { label: 'ACH Charge', icon: 'swap', to: '/ach-charge', capability: 'ach_initiate' },
];

const REPORT_NAV: ReportNavItem[] = [
  { kind: 'page', to: '/reports', label: 'Report Center', icon: 'chart', capability: 'manage_reports' },
  { kind: 'page', to: '/email-records', label: 'Email Records', icon: 'mail', capability: 'view_email_archive' },
  { kind: 'page', to: '/audit-log', label: 'Audit Log', icon: 'history', capability: 'view_audit_log' },
];

const PERFORMANCE_NAV: ReportNavItem = {
  kind: 'dashboard', key: 'developer_performance', label: 'Performance', icon: 'activity', capability: 'edit_settings',
};

/* ── Admin entry points shown in the main sidebar by matching capability ─ */
const ADMIN_GROUP = [
  { to: '/admin/reporting', label: 'Reports', icon: 'chart', access: 'admin_area' },
  { to: '/admin/users', label: 'Users & Roles', icon: 'users', access: 'user_management' },
  { to: '/admin/deal-close-review', label: 'Deal Close Requests', icon: 'check', access: 'review_deal_close_requests' },
  { to: '/admin/deal-prep', label: 'Deal Prep Intake', icon: 'inbox', access: 'deal_prep_admin' },
  { to: '/admin/tables', label: 'Admin Console', icon: 'settings', access: 'super_admin' },
];

/* ── Admin sidebar nav items (shown while inside /admin) ────────────── */
const ADMIN_NAV = [
  { to: '/admin/reporting', label: 'Reports', icon: 'chart', access: 'admin_area' },
  { to: '/admin/tables', label: 'Tables', icon: 'document', access: 'super_admin' },
  { to: '/admin/users', label: 'Users', icon: 'user', access: 'user_management' },
  { to: '/admin/groups', label: 'Groups', icon: 'users', access: 'permission_management' },
  { to: '/admin/permissions', label: 'Permissions', icon: 'shield', access: 'permission_management' },
  { to: '/admin/page-layouts', label: 'Page Layouts', icon: 'file', access: 'super_admin' },
  { to: '/admin/global-settings', label: 'Global Settings', icon: 'settings', access: 'super_admin' },
  { to: '/admin/exports', label: 'Exports', icon: 'download', access: 'export_grid_data' },
  { to: '/admin/email-templates', label: 'Email Templates', icon: 'mail', access: 'super_admin' },
  { to: '/admin/publications', label: 'Publications', icon: 'activity', access: 'super_admin' },
  { to: '/admin/ach-settings', label: 'ACH Settings', icon: 'dollar', access: 'super_admin' },
  { to: '/admin/gmail', label: 'Gmail', icon: 'mail', access: 'super_admin' },
  { to: '/admin/email-review', label: 'Email Review', icon: 'inbox', access: 'reconcile_ingestion' },
  { to: '/admin/deal-close-review', label: 'Deal Close Requests', icon: 'check', access: 'review_deal_close_requests' },
  { to: '/admin/deal-prep', label: 'Deal Prep Intake', icon: 'inbox', access: 'deal_prep_admin' },
];

/* ── Sidebar Component ──────────────────────────────────────────────── */
function Sidebar(): React.ReactElement | null {
  const { data: metadata } = useGlobalMetadata();
  const { appUser } = useAuth();
  const { hasCapability } = useMyCapabilities();
  const canExport = useCanExportGridData();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const canAccessAdminItem = (access: string): boolean => {
    if (access === 'admin_area') return canAccessAdminArea(Boolean(appUser?.is_super_admin), hasCapability);
    if (access === 'export_grid_data') return canExport;
    if (access === 'super_admin') return Boolean(appUser?.is_super_admin);
    if (access === 'permission_management') return hasCapability('edit_permissions');
    if (access === 'user_management') {
      return hasCapability('edit_permissions') || hasCapability('manage_users') || hasCapability('manage_openers');
    }
    if (access === 'review_deal_close_requests') return hasCapability('review_deal_close_requests');
    if (access === 'deal_prep_admin') return hasCapability('send_deal_prep_history');
    if (access === 'default_layouts') return hasCapability('manage_default_layouts');
    return hasCapability('reconcile_ingestion');
  };
  const repNavigationQuery = useQuery({
    queryKey: ['rep-only-navigation', appUser?.id],
    queryFn: () => loadRepOnlyNavigation(appUser!.id),
    enabled: Boolean(appUser?.id),
    ...queryPolicies.permissions,
    refetchOnWindowFocus: true,
  });

  const [expandedRoutes, setExpandedRoutes] = useState<Record<string, boolean>>({});
  const { schedule: scheduleDestinationWarm, cancel: cancelDestinationWarm } = useHoverPrefetch();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sky-sidebar-collapsed') === 'true'; } catch { return false; }
  });
  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('sky-sidebar-collapsed', String(next));
      } catch {
        // Keep the in-memory preference when storage is unavailable.
      }
      return next;
    });
  }, []);

  const tableToRouteName: Record<string, string> = metadata?.routeData?.tableToRouteName ?? {};
  const warmDestination = useCallback((to: string) => {
    if (!metadata) return;
    scheduleDestinationWarm(() => import('../utils/navigationPrefetch')
      .then(({ prefetchNavigationDestination }) => prefetchNavigationDestination(to, metadata)));
  }, [metadata, scheduleDestinationWarm]);

  // Get user initials for avatar
  const initials = useMemo(() => {
    const name = appUser?.name || '';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return 'U';
  }, [appUser?.name]);

  // Tooltip for collapsed sidebar
  const [tooltip, setTooltip] = useState<{ text: string; top: number } | null>(null);
  const tooltipTimeout = useRef<ReturnType<typeof setTimeout>>();
  const showTooltip = useCallback((e: React.MouseEvent, text: string) => {
    if (!collapsed) return;
    clearTimeout(tooltipTimeout.current);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({ text, top: rect.top + rect.height / 2 });
  }, [collapsed]);
  const hideTooltip = useCallback(() => {
    clearTimeout(tooltipTimeout.current);
    setTooltip(null);
  }, []);
  const leaveDestination = useCallback(() => {
    cancelDestinationWarm();
    hideTooltip();
  }, [cancelDestinationWarm, hideTooltip]);

  // Membership determines whether sensitive navigation is shown. Hold the
  // sidebar until that classification settles so rep-only users never see the
  // ordinary Rep/Lead Source links during initial loading.
  if (!metadata || (appUser?.id && repNavigationQuery.isLoading)) return null;

  const repNavigation = repNavigationQuery.data;
  const isRepOnly = repNavigation?.isRepOnly === true;
  // REG-922: the Instantly partner sees his leads page and his deals. UX
  // only: view_all_opportunities / view_partner_deals and RLS are the boundary.
  const isInstantlyPartner = repNavigation?.isInstantlyPartner === true;

  const renderRouteItem = (
    label: string,
    icon: string,
    routeName: string,
    segments?: RouteSegment[],
    options?: { to?: string; activeSearch?: Record<string, string>; disabledReason?: string },
  ) => {
    const reportSegments = (segments ?? []).flatMap((segment) => {
      const route = metadata.reportData?.byKey[segment.reportKey]?.route;
      return route ? [{ ...segment, route }] : [];
    });
    const hasChildren = reportSegments.length > 0;
    const activeSearchMatches = !options?.activeSearch || Object.entries(options.activeSearch)
      .every(([key, value]) => new URLSearchParams(location.search).get(key) === value);
    const isRouteActive = location.pathname === `/${routeName}` && activeSearchMatches;
    const childActive = reportSegments.some((segment) => segment.route === location.pathname);
    const isExpanded = hasChildren && !collapsed && (expandedRoutes[routeName] ?? childActive);

    return (
      <React.Fragment key={`${label}-${routeName}`}>
        <div className="sky-nav-item-wrap">
          {options?.disabledReason ? (
            <span
              className="sky-nav-item"
              aria-disabled="true"
              title={options.disabledReason}
              onMouseEnter={(e) => showTooltip(e, label)}
              onMouseLeave={hideTooltip}
              style={{ opacity: 0.55, cursor: 'not-allowed' }}
            >
              <SidebarIcon name={icon} />
              {!collapsed && <span>{label}</span>}
            </span>
          ) : (
            <NavLink
              to={options?.to ?? `/${routeName}`}
              className={() => `sky-nav-item ${isRouteActive ? 'active' : ''}`}
              onMouseEnter={(e) => {
                showTooltip(e, label);
                warmDestination(options?.to ?? `/${routeName}`);
              }}
              onFocus={() => warmDestination(options?.to ?? `/${routeName}`)}
              onBlur={cancelDestinationWarm}
              onMouseLeave={leaveDestination}
            >
              <SidebarIcon name={icon} />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          )}
          {hasChildren && !collapsed && (
            <button
              className="sky-nav-expand-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setExpandedRoutes(prev => ({ ...prev, [routeName]: !isExpanded }));
              }}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          )}
        </div>
        {isExpanded && reportSegments.map((segment) => {
          const segmentActive = segment.route === location.pathname;
          return (
            <NavLink
              key={segment.reportKey}
              to={segment.route}
              className={() => `sky-nav-item sky-nav-child ${segmentActive ? 'active' : ''}`}
              onMouseEnter={(e) => { showTooltip(e, segment.label); warmDestination(segment.route); }}
              onFocus={() => warmDestination(segment.route)}
              onBlur={cancelDestinationWarm}
              onMouseLeave={leaveDestination}
            >
              <span className="sky-nav-child-dot" />
              {!collapsed && <span>{segment.label}</span>}
            </NavLink>
          );
        })}
      </React.Fragment>
    );
  };

  const renderStaticItem = (label: string, icon: string, to: string, key?: string) => (
    <NavLink
      key={key ?? to}
      to={to}
      className={({ isActive }) => `sky-nav-item ${isActive ? 'active' : ''}`}
      onMouseEnter={(e) => { showTooltip(e, label); warmDestination(to); }}
      onFocus={() => warmDestination(to)}
      onBlur={cancelDestinationWarm}
      onMouseLeave={leaveDestination}
    >
      <SidebarIcon name={icon} />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );

  const resolveReportNavigation = (navigation: ReportNavItem[]) => navigation.reduce<Array<ReportNavItem & { to: string }>>((items, item) => {
    if (item.kind === 'page') {
      if (!item.capability || hasCapability(item.capability)) items.push(item);
      return items;
    }

    if (item.kind === 'dashboard') {
      const dashboard = metadata.dashboardData?.navItems.find(
        (candidate) => candidate.dashboard_key === item.key
      );
      if (dashboard && (!item.capability || hasCapability(item.capability))) {
        items.push({ ...item, to: dashboardPath(dashboard) });
      }
      return items;
    }

    if (item.kind === 'report') {
      const report = metadata.reportData?.navItems.find(
        (candidate) =>
          candidate.report_key === item.key
          || candidate.ui_label?.trim().toLowerCase() === item.label.toLowerCase()
      );
      const requiredCapability = (report as { required_capability?: string | null } | undefined)
        ?.required_capability;
      if (report && (!requiredCapability || hasCapability(requiredCapability))) {
        items.push({ ...item, to: reportPath(report.route, report.report_key) });
      }
      return items;
    }

    const routeName = tableToRouteName[item.table];
    const isVisible = !!routeName
      && (!item.capability || hasCapability(item.capability));
    if (isVisible) items.push({ ...item, to: `/${routeName}` });
    return items;
  }, []);
  const reportNavigation = resolveReportNavigation(REPORT_NAV);
  const performanceNavigation = resolveReportNavigation([PERFORMANCE_NAV])[0];
  // Rep-only users land on their personal rep dashboard (REG-641).
  const homePath = isInstantlyPartner ? '/partner-leads' : isRepOnly ? '/my-dashboard' : HOME_PATH;

  return (
    <aside className={`sky-sidebar ${collapsed ? 'sky-sidebar-collapsed' : ''}`}>
      {/* Logo — a consistent Home action from every route, including /admin. */}
      <div className="sky-sidebar-logo">
        <NavLink
          to={homePath}
          className="sky-logo-home"
          title="Go to the main dashboard"
          aria-label="Skyline CRM home"
          onMouseEnter={() => warmDestination(homePath)}
          onFocus={() => warmDestination(homePath)}
          onBlur={cancelDestinationWarm}
          onMouseLeave={cancelDestinationWarm}
        >
        <svg
          width="34"
          height="34"
          viewBox="0 0 34 34"
          fill="none"
          style={{ flexShrink: 0 }}
          aria-hidden="true"
        >
          <rect width="34" height="34" rx="8" fill="var(--accent)" />
          <g fill="rgba(0,0,0,0.45)">
            <rect x="5" y="20" width="3" height="8" rx="0.5" />
            <rect x="9" y="17" width="2" height="11" rx="0.5" />
            <rect x="22" y="18" width="3" height="10" rx="0.5" />
            <rect x="26" y="15" width="2" height="13" rx="0.5" />
          </g>
          <g fill="white">
            <rect x="4" y="22" width="4" height="6" rx="0.5" />
            <rect x="9" y="18" width="3" height="10" rx="0.5" />
            <rect x="13" y="10" width="4" height="18" rx="0.5" />
            <rect x="14.5" y="6" width="1" height="5" rx="0.5" />
            <rect x="17" y="15" width="3" height="13" rx="0.5" />
            <rect x="21" y="19" width="4" height="9" rx="0.5" />
            <rect x="26" y="16" width="3" height="12" rx="0.5" />
            <rect x="4" y="28" width="26" height="1.5" rx="0.5" />
          </g>
        </svg>
        {!collapsed && <div className="sky-logo-name">Skyline CRM</div>}
        </NavLink>
        {/* Rendered in both states — collapsing used to be the logo's job, and
            a collapsed sidebar was left with no way back. */}
        <button
          className="sky-collapse-btn"
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {collapsed ? (
              <>
                <polyline points="13 17 18 12 13 7" />
                <polyline points="6 17 11 12 6 7" />
              </>
            ) : (
              <>
                <polyline points="11 17 6 12 11 7" />
                <polyline points="18 17 13 12 18 7" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="sky-sidebar-scroll">
        {isInstantlyPartner ? (
          <>
            <div className="sky-nav-section">Core</div>
            {renderStaticItem('Leads', 'inbox', '/partner-leads', 'partner-leads')}
            {/* His Instantly deals: merchant / submission RLS (view_partner_deals) decides the rows. */}
            {tableToRouteName.merchant && renderRouteItem('Merchants', 'home', tableToRouteName.merchant)}
            {tableToRouteName.submission && renderRouteItem('Submissions', 'upload', tableToRouteName.submission)}
          </>
        ) : isAdmin ? (
          <>
            <div className="sky-nav-section">Admin</div>
            {ADMIN_NAV.filter((item) => canAccessAdminItem(item.access)).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `sky-nav-item ${isActive ? 'active' : ''}`
                }
                onMouseEnter={(e) => { showTooltip(e, item.label); warmDestination(item.to); }}
                onFocus={() => warmDestination(item.to)}
                onBlur={cancelDestinationWarm}
                onMouseLeave={leaveDestination}
              >
                <SidebarIcon name={item.icon} />
                <span>{item.label}</span>
              </NavLink>
            ))}
            {performanceNavigation && renderStaticItem(
              performanceNavigation.label,
              performanceNavigation.icon,
              performanceNavigation.to,
              'admin-performance',
            )}
          </>
        ) : (
          <>
            <div className="sky-nav-section">Core</div>
            {/* Rep-only users get exactly one Dashboard: the personal rep
                dashboard (REG-641). The business_command_center entry below is
                skipped for them even when they hold view_dashboard_navigation,
                so the sidebar never shows two Dashboard items (REG-663). */}
            {isRepOnly && renderStaticItem('Dashboard', 'dashboard', '/my-dashboard', 'my-rep-dashboard')}
            {MAIN_NAV.map((item) => {
              if ('kind' in item) {
                if (isRepOnly && item.kind === 'dashboard' && item.key === 'business_command_center') return null;
                const resolved = resolveReportNavigation([item])[0];
                return resolved
                  ? renderStaticItem(resolved.label, resolved.icon, resolved.to, `${resolved.kind}-${resolved.label}`)
                  : null;
              }
              if (item.capability && !hasCapability(item.capability)) return null;
              if (isRepOnly && item.table === 'lead_source') return null;
              if (item.to) return renderStaticItem(item.label, item.icon, item.to, item.label);
              const routeName = item.table ? tableToRouteName[item.table] : undefined;
              if (!routeName) return null;
              // Rep-only users get their own /my-dashboard instead of the
              // Rep workspace (REG-641) — the item is hidden, not narrowed.
              if (isRepOnly && item.table === 'rep') return null;
              return renderRouteItem(item.label, item.icon, routeName, item.segments);
            })}

            {reportNavigation.length > 0 && (
              <>
                <div className="sky-nav-section">Reports</div>
                {reportNavigation.map((item) =>
                  renderStaticItem(item.label, item.icon, item.to, `${item.kind}-${item.label}`)
                )}
              </>
            )}

            {ADMIN_GROUP.some((item) => canAccessAdminItem(item.access)) && (
              <>
                <div className="sky-nav-section">Admin</div>
                {ADMIN_GROUP.filter((item) => canAccessAdminItem(item.access))
                  .map((item) => renderStaticItem(item.label, item.icon, item.to, item.label))}
              </>
            )}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="sky-sidebar-footer">
        <div className="sky-avatar">{initials}</div>
        {!collapsed && (
          <>
            <div>
              <div className="sky-footer-name">{appUser?.name || 'User'}</div>
              <div className="sky-footer-role">
                {appUser?.is_super_admin ? 'Admin' : 'User'}
              </div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
              <div className="sky-live-dot" title="Live sync active" />
            </div>
          </>
        )}
      </div>
      {/* Tooltip portal */}
      {tooltip && createPortal(
        <div className="sky-sidebar-tooltip" style={{ top: tooltip.top }}>
          {tooltip.text}
        </div>,
        document.body
      )}
    </aside>
  );
}

export default Sidebar;
